import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import { aiArticles } from "../data/aiArticles";
import { seoConfig } from "../data/seoConfig";

export default function AINews() {
  const aiNewsArticles = aiArticles
    .filter((article) => article.path?.startsWith("/ai/"))
    .sort((a, b) => new Date(b.datePublished || 0) - new Date(a.datePublished || 0));
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "AI News", to: "/ai-news" }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "AI News, Tools and Model Guides",
    description: "Browse AI model explainers, tool documentation, research reports and robotics learning guides.",
    url: `${seoConfig.baseUrl}/ai-news`,
    mainEntity: aiNewsArticles.map((article) => ({
      "@type": "Article",
      headline: article.title,
      url: `${seoConfig.baseUrl}${article.path}`,
      datePublished: article.datePublished,
      author: { "@type": "Organization", name: "TechniaHQRobot" }
    }))
  };

  return (
    <>
      <SEO
        title="AI News, Models, Coding Tools and Comparisons"
        description="Browse AI model explainers, tool documentation, research reports and robotics learning guides."
        path="/ai-news"
        type="website"
        schema={[buildBreadcrumbSchema(breadcrumbs), schema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="AI news and model guides"
        title="AI news, models and tools"
        description="Articles on AI models and tools, research reports, practical workflows and robotics learning paths. Check each article for its publication date, sources and scope."
      />

      <section className="site-shell py-12 sm:py-16">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {aiNewsArticles.map((article) => (
            <article key={article.id} className="soft-card p-6 transition hover:border-blue-200">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">{article.category}</p>
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{article.readingTime}</span>
                {article.datePublished && (
                  <time dateTime={article.datePublished} className="text-xs text-neutral-500">
                    {new Intl.DateTimeFormat("en", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      timeZone: "UTC",
                    }).format(new Date(article.datePublished))}
                  </time>
                )}
              </div>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-neutral-950">{article.title}</h2>
              <p className="mt-3 text-sm leading-6 text-neutral-600">{article.description}</p>
              <Link
                to={article.path}
                className="mt-5 inline-flex rounded-full bg-neutral-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700"
              >
                Read full article
              </Link>
            </article>
          ))}
        </div>
      </section>
      <CTASection title="Follow TechniaHQRobot for researched technology guides" />
    </>
  );
}
